import { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, Download, Loader2 } from 'lucide-react';
import { apiGet } from '@/lib/api';

/**
 * Worksheets — the student side.
 *
 * Published by us against a chapter, exactly like infographics and cinegraphics,
 * so a sheet appears the moment that chapter is part of a portion. Nothing is
 * generated here: if we haven't published one yet, we say so plainly.
 */
export default function WorksheetsPage() {
  const [data, setData] = useState(null);
  const [chapter, setChapter] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await apiGet(`/premium/v1/worksheets${chapter ? `?chapter=${encodeURIComponent(chapter)}` : ''}`));
      setError('');
    } catch (e) { setError(e?.message || 'Worksheets are unavailable right now'); }
  }, [chapter]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10" data-testid="worksheets-error">
        <p className="font-heading text-base text-slate-800">We couldn't open your worksheets</p>
        <p className="font-body text-sm text-slate-500 mt-1">{error}</p>
        <button onClick={load} className="mt-3 text-sm font-heading text-indigo-600 hover:underline" data-testid="worksheets-retry">Try again</button>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 flex items-center gap-2 text-slate-400" data-testid="worksheets-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> <span className="font-body text-sm">Fetching your worksheets…</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="worksheets-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Worksheets</h1>
        <p className="font-body text-sm text-slate-500 mt-1">
          Printable practice for your chapters — written for your board and class, not scraped from anywhere.
        </p>
      </div>

      {data.chapters.length > 1 && (
        <div className="flex flex-wrap gap-1.5" data-testid="worksheets-chapter-filter">
          <button onClick={() => setChapter('')} data-testid="worksheets-chapter-all"
            className={`text-[11px] font-heading px-3 py-1 rounded-full border ${!chapter ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}>
            All chapters
          </button>
          {data.chapters.map(c => (
            <button key={c} onClick={() => setChapter(c)} data-testid={`worksheets-chapter-${c}`}
              className={`text-[11px] font-heading px-3 py-1 rounded-full border ${chapter === c ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}>
              {c}
            </button>
          ))}
        </div>
      )}

      {data.worksheets.length ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {data.worksheets.map(w => (
            <a key={w.worksheet_id} href={w.file_url} target="_blank" rel="noreferrer"
              data-testid={`worksheet-card-${w.worksheet_id}`}
              className="rounded-3xl border border-slate-200 bg-white p-4 hover:border-indigo-300 transition-colors block">
              <p className="font-heading text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
                <ClipboardCheck className="w-4 h-4 text-emerald-500" /> {w.title}
              </p>
              <p className="font-body text-[12px] text-slate-500 mt-1">
                {w.subject} · {w.chapter}
              </p>
              <p className="font-body text-[11px] text-slate-400 mt-0.5">
                {w.level}{w.questions ? ` · ${w.questions} questions` : ''}{w.answer_key_included ? ' · answer key included' : ''}
              </p>
              <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-heading text-indigo-600">
                <Download className="w-3 h-3" /> Open the sheet
              </span>
            </a>
          ))}
        </div>
      ) : (
        <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="worksheets-empty">
          <p className="font-body text-[13px] text-slate-600">{data.empty_message}</p>
        </div>
      )}
    </div>
  );
}
